import React, { FC, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Image, Button, baseVariables } from '@marriott/mi-ui-library';
import { useSession } from '@marriott/mi-headless-utils';
import { logger, useMediaQuery } from '@marriott/shared/mi-helper-utils';
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { getAEMTrackingProperties, isExternalLink, riottMediaDynamicMediaTransformation } from '../../utils/helpers';
import { RIOTT_MEDIA_PARAMETERS } from '../../utils/constants';
import { ITrackingProperties, RiottMediaProps, Rendition } from './RiottMediaCard.types';
import { StyledRiottMedia } from './RiottMediaCard.styles';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';

export const RiottMediaComponent: FC<RiottMediaProps> = props => {
  const { datalayer } = useSession();
  const {
    mboxParameter,
    metaData,
    image,
    destinationUrl1,
    adData,
    openInNewTab = true,
    trackingProperties = {} as ITrackingProperties,
    isTargetContentLoaded = mboxParameter && !AuthoringUtils?.isInEditor() ? false : true,
    isTargetContentError = false,
    displayEvergreenContent = true,
    styleclass,
  } = props;

  const isDesktop = useMediaQuery(baseVariables.mediaQuery.xl);
  const isTablet = useMediaQuery(baseVariables.mediaQuery.md);

  const { log } = logger({})('riottMedia');

  const riottMediaTrackingEndpoint = RIOTT_MEDIA_PARAMETERS.URL || '';

  const ref = useRef<HTMLAnchorElement>(null);
  const [isVisible, componentRef] = useIntersectionObserver();

  const [riottMediaAttributes, setRiottMediaAttributes] = useState<{
    showRiottMediaComponent: boolean;
    isRiottMediaFromMbox: boolean;
    loading: boolean;
    riottMediaAssets: {
      altText: string;
      assetPath: string;
      renditions: Rendition[];
    } | null;
  }>({
    showRiottMediaComponent: AuthoringUtils?.isInEditor() ? true : false,
    isRiottMediaFromMbox: false,
    loading: true,
    riottMediaAssets: null,
  });

  useEffect(() => {
    if (isTargetContentLoaded && metaData?.isMango && !isTargetContentError) {
      const riottMediaAssets = riottMediaDynamicMediaTransformation(adData?.[0]?.assets);
      const desktopRendition = riottMediaAssets?.renditions?.[0];

      const isRiottMediaFromMbox =
        riottMediaAssets && riottMediaAssets?.renditions?.length > 0 && desktopRendition?.renditionPath ? true : false;
      const showRiottMediaComponent = isRiottMediaFromMbox
        ? true
        : displayEvergreenContent && image?.assetPath
        ? true
        : false;

      handleRiottMediaTracking(
        showRiottMediaComponent,
        isRiottMediaFromMbox,
        adData?.[0]?.custom_tracking_url,
        RIOTT_MEDIA_PARAMETERS.LOADED
      );

      setRiottMediaAttributes({
        ...riottMediaAttributes,
        showRiottMediaComponent: showRiottMediaComponent,
        isRiottMediaFromMbox: isRiottMediaFromMbox,
        loading: !isTargetContentLoaded,
        riottMediaAssets: riottMediaAssets,
      });
    } else if (isTargetContentLoaded) {
      setRiottMediaAttributes({
        ...riottMediaAttributes,
        showRiottMediaComponent: displayEvergreenContent && image?.assetPath ? true : false,
        isRiottMediaFromMbox: false,
        loading: !isTargetContentLoaded,
        riottMediaAssets: null,
      });
    }
  }, [isTargetContentLoaded]);

  useEffect(() => {
    if (isVisible) {
      // This is for Analytics tracking
      handleRiottMediaTracking(
        riottMediaAttributes.showRiottMediaComponent,
        riottMediaAttributes.isRiottMediaFromMbox,
        adData?.[0]?.impression_tracking_url
      );
    }
  }, [isVisible]);

  const trackingPropertiesDynamic = getAEMTrackingProperties(
    trackingProperties,
    datalayer?.['mr_prof_authentication_state'] === 'authenticated'
  );

  const handleRiottMediaTracking = (
    showRiottMediaComponent: boolean,
    isRiottMediaFromMbox: boolean,
    trackingURLFromUXL: string,
    customTracking?: string
  ) => {
    try {
      if (showRiottMediaComponent && isRiottMediaFromMbox && trackingURLFromUXL && trackingURLFromUXL !== '') {
        const riottMediaHeaders = new Headers();
        const urlObj = new URL(trackingURLFromUXL);
        const newParams = new URLSearchParams();
        const keyArray: { trackingData: string; clientName: string; [type: string]: string | undefined } = {
          trackingData: adData?.[0]?.tracking_data,
          clientName: RIOTT_MEDIA_PARAMETERS.CLIENT_NAME,
          type: customTracking,
        };

        urlObj?.searchParams?.forEach((value: string, key: string) => {
          if (value === `{${key}}`) {
            if (key in keyArray && keyArray?.[key]) {
              newParams?.set(key, keyArray[key] as string);
            }
          } else if (key === RIOTT_MEDIA_PARAMETERS.key && keyArray?.[key]) {
            newParams?.set(key, keyArray[key] as string);
          } else if (value !== `{${key}}`) {
            newParams?.set(key, value);
          }
        });

        const trackingURL = `${riottMediaTrackingEndpoint}?${newParams?.toString()}`;

        fetch(trackingURL, {
          method: 'GET',
          headers: riottMediaHeaders,
        });
      }
    } catch (error) {
      log.error('Error in mango pilot tracking api call:', error);
    }
  };

  const getScreenSizeBasedURL = () => {
    const assets = adData?.[0]?.assets;
    let URL = assets?.primaryImage_desktop_landing_page_url || assets?.ctaURL;
    if (isDesktop) {
      URL = assets?.primaryImage_desktop
        ? assets?.primaryImage_desktop_landing_page_url || assets?.ctaURL
        : assets?.ctaURL;
    } else if (isTablet) {
      URL = assets?.primaryImage_tablet
        ? assets?.primaryImage_tablet_landing_page_url || assets?.ctaURL
        : assets?.primaryImage_desktop_landing_page_url || assets?.ctaURL;
    } else {
      URL = assets?.primaryImage_mobile
        ? assets?.primaryImage_mobile_landing_page_url || assets?.ctaURL
        : assets?.primaryImage_desktop_landing_page_url || assets?.ctaURL;
    }
    return URL;
  };

  return (
    <StyledRiottMedia
      data-testid="merch-RiottMedia"
      data-component-name="o-merch-RiottMedia"
      className={clsx(
        `justify-content-center riott-media ${
          isTargetContentError && String(displayEvergreenContent) === 'false' ? 'd-none' : 'd-flex'
        }`
      )}
      ref={componentRef}
    >
      {riottMediaAttributes.showRiottMediaComponent || AuthoringUtils?.isInEditor() ? (
        <Button
          isLink={true}
          testId="button"
          callback={() =>
            handleRiottMediaTracking(
              riottMediaAttributes.showRiottMediaComponent,
              riottMediaAttributes.isRiottMediaFromMbox,
              adData?.[0]?.click_tracking_url
            )
          }
          ref={ref}
          href={AuthoringUtils.isInEditor() ? destinationUrl1 : getScreenSizeBasedURL()}
          target={
            isExternalLink(AuthoringUtils.isInEditor() ? destinationUrl1 : getScreenSizeBasedURL(), openInNewTab)
              ? '_blank'
              : '_self'
          }
          className={`${styleclass} riott-media__width ${
            AuthoringUtils.isInEditor() ? 'riott-media__authoring_container' : ''
          }`}
          trackingProperties={{ ...trackingPropertiesDynamic, isMango: true, mboxName: mboxParameter }}
        >
          <div className={clsx('riott-media__image_container')}>
            {riottMediaAttributes.isRiottMediaFromMbox && riottMediaAttributes.riottMediaAssets ? (
              <Image
                customClass={clsx('riott-media__image')}
                defaultImageURL={riottMediaAttributes?.riottMediaAssets?.assetPath}
                altText={riottMediaAttributes?.riottMediaAssets?.altText}
                renditions={riottMediaAttributes?.riottMediaAssets?.renditions}
              />
            ) : (
              <Image
                customClass={clsx('riott-media__image')}
                altText={image?.altText ?? ''}
                renditions={image?.renditions}
                dynamic={image?.dynamic}
                defaultImageURL={image?.assetPath}
              />
            )}
          </div>
        </Button>
      ) : (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
          {riottMediaAttributes.loading && (
            <div className="d-flex align-items-center riott-media__skelton">
              <a className={`riott-media__width ${styleclass}`}>
                <div className="riott-media__skelton-image skeleton-loader-composite"></div>
              </a>
            </div>
          )}
        </>
      )}
    </StyledRiottMedia>
  );
};

const RiottMediaConfig = {
  emptyLabel: 'RiottMediaCard',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/riottmediacard`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const RiottMediaCard = (props: any) => {
  return (
    <EditableComponent
      config={RiottMediaConfig}
      {...props}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      children={<RiottMediaComponent {...({} as any)} />}
    />
  );
};
